import { useMemo } from 'react';
import { Autocomplete, AutocompleteProps, TextField, SxProps, Theme } from '@mui/material';
import { ICasSelect } from './CasSelect';

/*
  Multi-select filter control. Fits the CasTable filter contract: `value` is an
  array of ids and `onChange(event, ids)` emits the selected ids. An empty array
  means "no filter" (optional by default) — no separate All option is needed.
  Option-shape props (options, optionsIdField/NameField) follow ICasSelect.
*/
export interface ICasMultiSelect
  extends Omit<ICasSelect, 'value' | 'onChange' | 'label' | 'emptyMessage'> {
  value?: (number | string)[] | null;
  label?: string;
  onChange?: (event: any, value: (number | string)[]) => void;
  loading?: boolean;
  sx?: SxProps<Theme>;
  // Optional: how each option is drawn in the list, and each selected value in the field.
  renderOption?: AutocompleteProps<any, true, false, false>['renderOption'];
  renderTags?: AutocompleteProps<any, true, false, false>['renderTags'];
}

const CasMultiSelect = ({
  className,
  label,
  value,
  onChange,
  name,
  options = [],
  optionsNameField = 'name',
  optionsIdField = 'id',
  placeholder,
  loading,
  sx,
  mode = 'input',
  renderOption,
  renderTags,
}: ICasMultiSelect) => {
  const ids = Array.isArray(value) ? value : [];
  const selected = useMemo(() => {
    const idSet = new Set(ids);
    return options.filter((o) => idSet.has(o[optionsIdField]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options, value, optionsIdField]);

  // Comma-separated names, so read-only surfaces (filter chips) can show
  // "Hot, New" instead of a count or raw ids.
  if (mode === 'display') return <>{selected.map((o) => o[optionsNameField]).join(', ')}</>;

  return (
    <Autocomplete
      multiple
      size="small"
      className={className}
      options={options}
      loading={loading}
      value={selected}
      getOptionLabel={(o: any) => o?.[optionsNameField] ?? ''}
      isOptionEqualToValue={(o: any, v: any) => o[optionsIdField] === v[optionsIdField]}
      onChange={(event, sel: any[]) => onChange?.(event, sel.map((o) => o[optionsIdField]))}
      /*
        A selected option is a neutral token, not a status badge. The theme styles a filled
        default Chip as white-on-text.primary (near black - see
        theme/overrides/components/chip.tsx); "soft" is the theme's own quiet grey pill.
        Same treatment as the compose dialog's To/CC recipients (story 15380).
      */
      ChipProps={{ variant: 'soft' as const }}
      renderOption={renderOption}
      renderTags={renderTags}
      renderInput={(params) => (
        <TextField {...params} name={name} label={label} placeholder={ids.length ? undefined : placeholder} />
      )}
      sx={{ minWidth: 180, ...sx }}
    />
  );
};

export default CasMultiSelect;
